import { useMemo, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { LuPlus, LuTrash2 } from 'react-icons/lu';
import * as Yup from 'yup';

import { BillingGroup, BillingGroupAccountRequest, BillingGroupSaveRequest, GUID, Tenant } from '@ndvr/billing-api';
import { Account, LifecycleStatus } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormLabel } from '@/components/ui/form';
import { FormInput, Input } from '@/components/ui/input';
import {
    FormSelect,
    Select,
    SelectContent,
    SelectItem,
    SelectOption,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { convertFormDateToServerDate, convertServerDateToFormDate } from 'utils/date-formatter';
import { useFormWithYup } from 'utils/hooks/useFormWithYup';
import { useFeeSchedules } from 'views/billing/fee-schedules/fee-schedule-queries';
import { useAccountsForTenant } from 'views/portfolio/account-queries';

import { useBillingGroups, useCreateBillingGroup, useUpdateBillingGroup } from './billing-group-queries';
import { resolveAccountName } from './utils';

const NO_SETTLEMENT_OVERRIDE = '__none__';

const isHeldAway = (account: Account): boolean =>
    account.lifecycleStatus === LifecycleStatus.DRAFT && account.externallyManaged;

const isBillable = (account: Account): boolean =>
    account.lifecycleStatus === LifecycleStatus.REAL ||
    account.lifecycleStatus === LifecycleStatus.CLOSED ||
    isHeldAway(account);

const getStatusLabel = (account: Account): string | undefined => {
    if (account.lifecycleStatus === LifecycleStatus.CLOSED) {
        return 'Closed';
    }
    if (isHeldAway(account)) {
        return 'Held Away';
    }
    return undefined;
};

const schema = Yup.object({
    billingGroupName: Yup.string().trim().required('Required'),
    // FormSelect seeds an empty string when nothing is picked. Validate with a flat
    // `mixed` test so the message lands on this field (an object().required() error
    // nests under value/label, leaving FormMessage to render a bare "undefined").
    feeScheduleOption: Yup.mixed<SelectOption>().test(
        'required',
        'Required',
        (value) => typeof value === 'object' && value !== null && !!value.value,
    ),
    accounts: Yup.array()
        .of(
            Yup.object({
                accountGUID: Yup.string().required(),
                settlementAccountGUIDOverride: Yup.string().test(
                    'required-for-held-away',
                    'Held-away accounts must have a settlement account override',
                    function (value) {
                        const { heldAwayAccountGUIDs } = this.options.context as FormContext;
                        return !heldAwayAccountGUIDs.has(this.parent.accountGUID as string) || !!value;
                    },
                ),
                billingStartDate: Yup.string(),
                billingEndDate: Yup.string().test(
                    'date-order',
                    'End date must not be before start date',
                    function (end) {
                        const start = this.parent.billingStartDate as string | undefined;
                        if (!start || !end) return true;
                        return start <= end;
                    },
                ),
            }),
        )
        .required(),
});

type AccountRow = {
    accountGUID: string;
    settlementAccountGUIDOverride: string;
    billingStartDate: string;
    billingEndDate: string;
};

type FormValues = {
    billingGroupName: string;
    feeScheduleOption: SelectOption;
    accounts: AccountRow[];
};

type FormContext = {
    heldAwayAccountGUIDs: Set<string>;
};

interface CreateBillingGroupDialogProps {
    tenantGUID: GUID<Tenant>;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    billingGroup?: BillingGroup;
}

export const CreateBillingGroupDialog = ({
    tenantGUID,
    open,
    onOpenChange,
    billingGroup,
}: CreateBillingGroupDialogProps) => {
    const isEdit = !!billingGroup;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-[720px]" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Billing Group' : 'Create Billing Group'}</DialogTitle>
                </DialogHeader>

                {open && (
                    <BillingGroupForm
                        tenantGUID={tenantGUID}
                        billingGroup={billingGroup}
                        onClose={() => onOpenChange(false)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

interface BillingGroupFormProps {
    tenantGUID: GUID<Tenant>;
    billingGroup?: BillingGroup;
    onClose: () => void;
}

const BillingGroupForm = ({ tenantGUID, billingGroup, onClose }: BillingGroupFormProps) => {
    const isEdit = !!billingGroup;
    const { data: feeSchedules = [] } = useFeeSchedules();
    const { data: tenantAccounts = [] } = useAccountsForTenant(tenantGUID);
    const { data: billingGroups = [] } = useBillingGroups();
    const createMutation = useCreateBillingGroup(tenantGUID);
    const updateMutation = useUpdateBillingGroup();
    const isPending = createMutation.isPending || updateMutation.isPending;

    const accountByGUID = useMemo(() => new Map(tenantAccounts.map((a) => [a.accountGUID, a])), [tenantAccounts]);
    const displayName = (accountGUID: string) => {
        const account = accountByGUID.get(accountGUID);
        return account ? resolveAccountName(account) : accountGUID;
    };
    const statusLabel = (accountGUID: string) => {
        const account = accountByGUID.get(accountGUID);
        return account ? getStatusLabel(account) : undefined;
    };
    const isRealAccount = (accountGUID: string) =>
        accountByGUID.get(accountGUID)?.lifecycleStatus === LifecycleStatus.REAL;

    // An account may belong to only one billing group. Hide accounts already
    // assigned to a different group; keep those in the group being edited.
    const availableAccounts = useMemo(() => {
        const assignedElsewhere = new Set(
            billingGroups
                .filter((bg) => bg.billingGroupGUID !== billingGroup?.billingGroupGUID)
                .flatMap((bg) => bg.accounts.map((a) => a.accountGUID)),
        );
        return tenantAccounts
            .filter((a) => isBillable(a) && !assignedElsewhere.has(a.accountGUID))
            .sort((a, b) =>
                resolveAccountName(a).localeCompare(resolveAccountName(b), undefined, { sensitivity: 'base' }),
            );
    }, [tenantAccounts, billingGroups, billingGroup]);

    const heldAwayAccountGUIDs = useMemo(
        () => new Set(tenantAccounts.filter(isHeldAway).map((a) => a.accountGUID)),
        [tenantAccounts],
    );

    const feeScheduleOptions: SelectOption[] = feeSchedules.map((fs) => ({
        value: fs.feeScheduleGUID,
        label: fs.feeScheduleName,
    }));

    const form = useFormWithYup<FormValues, FormContext>(schema, undefined, {
        context: { heldAwayAccountGUIDs },
        defaultValues: {
            billingGroupName: billingGroup?.billingGroupName ?? '',
            feeScheduleOption: billingGroup
                ? {
                      value: billingGroup.feeSchedule.feeScheduleGUID,
                      label: billingGroup.feeSchedule.feeScheduleName,
                  }
                : (undefined as unknown as SelectOption),
            accounts:
                billingGroup?.accounts.map((a) => ({
                    accountGUID: a.accountGUID,
                    settlementAccountGUIDOverride: a.settlementAccountGUIDOverride ?? '',
                    // Server dates come back as "yyyy-MM-dd[Z]"; the date input expects a bare ISO date.
                    billingStartDate: a.billingStartDate ? convertServerDateToFormDate(a.billingStartDate) : '',
                    billingEndDate: a.billingEndDate ? convertServerDateToFormDate(a.billingEndDate) : '',
                })) ?? [],
        },
    });

    const { fields, append, remove } = useFieldArray({ control: form.control, name: 'accounts' });
    const selectedAccounts = form.watch('accounts');

    const [search, setSearch] = useState('');
    const filteredAccounts = useMemo(() => {
        const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
        if (terms.length === 0) return availableAccounts;
        return availableAccounts.filter((a) => {
            const haystack = `${resolveAccountName(a)} ${a.custodyAccountId ?? ''}`.toLowerCase();
            return terms.every((t) => haystack.includes(t));
        });
    }, [availableAccounts, search]);

    const removeAccount = (accountGUID: string) => {
        const index = fields.findIndex((f) => f.accountGUID === accountGUID);
        if (index < 0) return;
        // Clear settlement overrides that pointed at the account being removed.
        form.getValues('accounts').forEach((row, i) => {
            if (row.settlementAccountGUIDOverride === accountGUID) {
                form.setValue(`accounts.${i}.settlementAccountGUIDOverride`, '');
            }
        });
        remove(index);
    };

    const addAccount = (accountGUID: string) => {
        if (fields.some((f) => f.accountGUID === accountGUID)) return;
        append({
            accountGUID,
            settlementAccountGUIDOverride: '',
            billingStartDate: '',
            billingEndDate: '',
        });
    };

    const onSubmit = async (data: FormValues) => {
        const selectedGUIDs = new Set(data.accounts.map((a) => a.accountGUID));
        const accounts: BillingGroupAccountRequest[] = data.accounts.map((row) => {
            const settlement =
                row.settlementAccountGUIDOverride &&
                row.settlementAccountGUIDOverride !== row.accountGUID &&
                selectedGUIDs.has(row.settlementAccountGUIDOverride)
                    ? row.settlementAccountGUIDOverride
                    : undefined;
            return {
                accountGUID: row.accountGUID,
                settlementAccountGUIDOverride: settlement,
                billingStartDate: row.billingStartDate ? convertFormDateToServerDate(row.billingStartDate) : undefined,
                billingEndDate: row.billingEndDate ? convertFormDateToServerDate(row.billingEndDate) : undefined,
            };
        });

        const request: BillingGroupSaveRequest = {
            billingGroupName: data.billingGroupName,
            feeScheduleGUID: data.feeScheduleOption.value,
            accounts,
        };

        if (isEdit) {
            await updateMutation.mutateAsync({ billingGroupGUID: billingGroup.billingGroupGUID, request });
        } else {
            await createMutation.mutateAsync(request);
        }
        onClose();
    };

    const accountErrors = form.formState.errors.accounts;

    return (
        <Form {...form}>
            <form className="flex min-h-0 flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-4 overflow-y-auto pr-1">
                    <FormInput formMethods={form} name="billingGroupName" label="Name*" required />
                    <FormSelect
                        formMethods={form}
                        name="feeScheduleOption"
                        label="Fee Schedule*"
                        placeholder="Select a fee schedule"
                        options={feeScheduleOptions}
                        rules={{ required: true }}
                    />

                    <Separator />

                    <div className="flex flex-col gap-2">
                        <FormLabel>Available Accounts</FormLabel>
                        <div className="overflow-hidden rounded-md border">
                            <Input
                                placeholder="Search accounts…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="rounded-none border-0 border-b focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                            <div className="max-h-48 overflow-y-auto">
                                {filteredAccounts.length === 0 ? (
                                    <p className="text-muted-foreground p-3 text-sm">No accounts available.</p>
                                ) : (
                                    filteredAccounts.map((account) => {
                                        const added = selectedAccounts.some(
                                            (a) => a.accountGUID === account.accountGUID,
                                        );
                                        const name = resolveAccountName(account);
                                        const accountStatusLabel = getStatusLabel(account);
                                        return (
                                            <button
                                                type="button"
                                                key={account.accountGUID}
                                                disabled={added}
                                                onClick={() => addAccount(account.accountGUID)}
                                                className="group hover:bg-muted/50 flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <span className="flex-1">{name}</span>
                                                {accountStatusLabel && (
                                                    <span className="text-muted-foreground text-xs">
                                                        {accountStatusLabel}
                                                    </span>
                                                )}
                                                {account.custodyAccountId && (
                                                    <span className="text-muted-foreground text-xs">
                                                        {account.custodyAccountId}
                                                    </span>
                                                )}
                                                {added ? (
                                                    <span className="text-muted-foreground text-xs">Added</span>
                                                ) : (
                                                    <LuPlus className="text-muted-foreground h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
                                                )}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>

                    {fields.length > 0 && (
                        <div className="flex flex-col gap-3">
                            <FormLabel>Selected Accounts ({fields.length})</FormLabel>
                            {fields
                                .map((field, index) => ({ field, index }))
                                .sort((a, b) =>
                                    displayName(a.field.accountGUID).localeCompare(
                                        displayName(b.field.accountGUID),
                                        undefined,
                                        { sensitivity: 'base' },
                                    ),
                                )
                                .map(({ field, index }) => {
                                    const rowErrors = accountErrors?.[index];
                                    const accountStatusLabel = statusLabel(field.accountGUID);
                                    const settlementOptions = selectedAccounts
                                        .filter(
                                            (a) => a.accountGUID !== field.accountGUID && isRealAccount(a.accountGUID),
                                        )
                                        .sort((a, b) =>
                                            displayName(a.accountGUID).localeCompare(
                                                displayName(b.accountGUID),
                                                undefined,
                                                { sensitivity: 'base' },
                                            ),
                                        );
                                    return (
                                        <div key={field.id} className="flex flex-col gap-3 rounded-md border p-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium">
                                                    {displayName(field.accountGUID)}
                                                    {accountStatusLabel && (
                                                        <span className="text-muted-foreground ml-2 text-xs font-normal">
                                                            {accountStatusLabel}
                                                        </span>
                                                    )}
                                                </span>
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 shrink-0"
                                                    onClick={() => removeAccount(field.accountGUID)}
                                                >
                                                    <LuTrash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div className="col-span-2 flex flex-col gap-1">
                                                    <span className="text-muted-foreground text-xs font-medium">
                                                        Settlement account override
                                                    </span>
                                                    <Controller
                                                        control={form.control}
                                                        name={`accounts.${index}.settlementAccountGUIDOverride`}
                                                        render={({ field: { value, onChange } }) => (
                                                            <Select
                                                                value={value || NO_SETTLEMENT_OVERRIDE}
                                                                onValueChange={(v) =>
                                                                    onChange(v === NO_SETTLEMENT_OVERRIDE ? '' : v)
                                                                }
                                                            >
                                                                <SelectTrigger>
                                                                    <SelectValue placeholder="None" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value={NO_SETTLEMENT_OVERRIDE}>
                                                                        None
                                                                    </SelectItem>
                                                                    {settlementOptions.map((a) => (
                                                                        <SelectItem
                                                                            key={a.accountGUID}
                                                                            value={a.accountGUID}
                                                                        >
                                                                            {displayName(a.accountGUID)}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        )}
                                                    />
                                                    {rowErrors?.settlementAccountGUIDOverride && (
                                                        <p className="text-destructive text-xs">
                                                            {rowErrors.settlementAccountGUIDOverride.message}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-muted-foreground text-xs font-medium">
                                                        Billing start date
                                                    </span>
                                                    <Controller
                                                        control={form.control}
                                                        name={`accounts.${index}.billingStartDate`}
                                                        render={({ field: { value, onChange } }) => (
                                                            <Input
                                                                type="date"
                                                                value={value}
                                                                onChange={(e) => onChange(e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-muted-foreground text-xs font-medium">
                                                        Billing end date
                                                    </span>
                                                    <Controller
                                                        control={form.control}
                                                        name={`accounts.${index}.billingEndDate`}
                                                        render={({ field: { value, onChange } }) => (
                                                            <Input
                                                                type="date"
                                                                value={value}
                                                                onChange={(e) => onChange(e.target.value)}
                                                            />
                                                        )}
                                                    />
                                                    {rowErrors?.billingEndDate && (
                                                        <p className="text-destructive text-xs">
                                                            {rowErrors.billingEndDate.message}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isPending}>
                        {isPending ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save Changes' : 'Create'}
                    </Button>
                </DialogFooter>
            </form>
        </Form>
    );
};
